import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ToastrService } from 'ngx-toastr';
import { TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import { AuthService } from '@app/core/services/auth.service';
import { HTTP_STATUS_CODE } from '@app/core/constants/system.constants';

@Component({
  selector: 'app-profile',
  templateUrl: './profile.component.html',
  styleUrls: ['./profile.component.scss'],
})
export class ProfileComponent implements OnInit, OnDestroy {
  form!: FormGroup;
  isLoading = false;
  isSubmitted = false;
  otpSent = false;
  otpDuration = 0;
  maskedOtpEmail = '';
  contactEmail = '';
  fullName = '';
  originalPersonalEmail = '';
  originalMobileNumber = '';
  otpEnabled = false;
  private emailSub?: Subscription;

  constructor(
    private fb: FormBuilder,
    private toastr: ToastrService,
    private translate: TranslateService,
    private authService: AuthService,
  ) {}

  ngOnInit(): void {
    this.form = this.fb.group({
      personalEmail: ['', [Validators.required, Validators.email]],
      mobileNumber: ['', [Validators.required, Validators.pattern(/^\+?[\d\s\-()]{9,20}$/)]],
      otpCode: [''],
    });
    this.emailSub = this.form.get('personalEmail')!.valueChanges.subscribe(() => {
      this.otpSent = false;
      this.form.get('otpCode')?.reset('');
      this.updateOtpValidators();
    });
    this.loadContact();
  }

  ngOnDestroy(): void {
    this.emailSub?.unsubscribe();
  }

  get f() {
    return this.form.controls;
  }

  get needOtp(): boolean {
    return this.otpEnabled && this.emailChanged;
  }

  get emailChanged(): boolean {
    const current = (this.form?.get('personalEmail')?.value || '').trim().toLowerCase();
    const original = (this.originalPersonalEmail || '').trim().toLowerCase();
    return current !== original;
  }

  get mobileChanged(): boolean {
    const current = (this.form?.get('mobileNumber')?.value || '').trim();
    const original = (this.originalMobileNumber || '').trim();
    return current !== original;
  }

  get hasChanges(): boolean {
    return this.emailChanged || this.mobileChanged;
  }

  /** Nút lưu chỉ bật khi form hợp lệ, có thay đổi, và đã hoàn tất OTP (nếu cần). */
  get canSave(): boolean {
    if (this.isLoading || !this.form || this.form.invalid || !this.hasChanges) {
      return false;
    }
    if (this.needOtp) {
      const otp = (this.form.get('otpCode')?.value || '').trim();
      return this.otpSent && !!otp;
    }
    return true;
  }

  get saveHint(): string {
    if (!this.hasChanges) {
      return 'Chưa có thay đổi để lưu.';
    }
    if (this.form.get('personalEmail')?.invalid || this.form.get('mobileNumber')?.invalid) {
      return 'Vui lòng kiểm tra lại thông tin đã nhập.';
    }
    if (this.needOtp && !this.otpSent) {
      return 'Bạn đã đổi email cá nhân. Vui lòng nhấn "Gửi OTP" và nhập mã xác nhận trước khi lưu.';
    }
    if (this.needOtp && this.otpSent && !(this.form.get('otpCode')?.value || '').trim()) {
      return 'Vui lòng nhập mã OTP đã gửi tới email cá nhân mới.';
    }
    return '';
  }

  loadContact(): void {
    this.isLoading = true;
    this.authService.getMyContact().subscribe({
      next: (res) => {
        this.isLoading = false;
        if (res?.code !== HTTP_STATUS_CODE.SUCCESS) {
          this.toastr.error(res?.message || this.translate.instant('common.error.system'));
          return;
        }
        const data = res.data || {};
        this.contactEmail = data.email || '';
        this.fullName = data.fullName || '';
        this.originalPersonalEmail = data.personalEmail || '';
        this.originalMobileNumber = data.mobileNumber || '';
        this.otpEnabled = !!data.otpEnabled;
        this.form.patchValue(
          {
            personalEmail: data.personalEmail || '',
            mobileNumber: data.mobileNumber || '',
          },
          { emitEvent: false },
        );
        this.otpSent = false;
        this.updateOtpValidators();
      },
      error: () => {
        this.isLoading = false;
        this.toastr.error(this.translate.instant('common.error.system'));
      },
    });
  }

  sendOtp(): void {
    this.isSubmitted = true;
    const emailCtrl = this.form.get('personalEmail');
    if (emailCtrl?.invalid) {
      this.toastr.warning('Vui lòng nhập email cá nhân hợp lệ trước khi gửi OTP');
      return;
    }
    if (!this.needOtp) {
      this.toastr.info('Hệ thống không yêu cầu OTP khi cập nhật liên hệ');
      return;
    }
    this.isLoading = true;
    this.authService.sendContactOtp(emailCtrl!.value.trim()).subscribe({
      next: (res) => {
        this.isLoading = false;
        if (res?.code !== HTTP_STATUS_CODE.SUCCESS) {
          this.toastr.error(res?.message);
          return;
        }
        this.otpSent = true;
        this.otpDuration = res?.data?.duration || 90;
        this.maskedOtpEmail = res?.data?.email || emailCtrl!.value;
        this.updateOtpValidators();
        this.toastr.success('Đã gửi mã OTP tới ' + this.maskedOtpEmail);
      },
      error: (err) => this.handleError(err),
    });
  }

  submitUpdate(): void {
    this.isSubmitted = true;
    this.updateOtpValidators();

    if (!this.hasChanges) {
      this.toastr.info('Chưa có thay đổi để lưu');
      return;
    }
    if (this.form.invalid) {
      this.toastr.warning('Vui lòng kiểm tra lại thông tin đã nhập');
      return;
    }
    if (this.needOtp && !this.otpSent) {
      this.toastr.warning('Bạn đã đổi email cá nhân. Vui lòng gửi mã OTP trước khi lưu thay đổi');
      return;
    }
    if (this.needOtp && !(this.form.get('otpCode')?.value || '').trim()) {
      this.toastr.warning('Vui lòng nhập mã OTP đã gửi tới email cá nhân mới');
      return;
    }

    this.isLoading = true;
    const value = this.form.value;
    this.authService
      .updateContact({
        personalEmail: value.personalEmail.trim(),
        mobileNumber: value.mobileNumber.trim(),
        otpCode: this.needOtp ? (value.otpCode || '').trim() : '',
      })
      .subscribe({
        next: (res) => {
          this.isLoading = false;
          if (res?.code === HTTP_STATUS_CODE.SUCCESS) {
            this.originalPersonalEmail = value.personalEmail.trim();
            this.originalMobileNumber = value.mobileNumber.trim();
            this.otpSent = false;
            this.form.get('otpCode')?.reset('');
            this.updateOtpValidators();
            this.toastr.success('Đã cập nhật thông tin cá nhân thành công');
            return;
          }
          this.toastr.error(res?.message || 'Cập nhật thất bại. Vui lòng thử lại');
        },
        error: (err) => this.handleError(err),
      });
  }

  private updateOtpValidators(): void {
    const otpCtrl = this.form.get('otpCode');
    if (this.needOtp) {
      otpCtrl?.setValidators([Validators.required]);
    } else {
      otpCtrl?.clearValidators();
    }
    otpCtrl?.updateValueAndValidity({ emitEvent: false });
  }

  private handleError(err: any): void {
    this.isLoading = false;
    this.toastr.error(err?.error?.message || this.translate.instant('common.error.system'));
  }
}
